import React from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaUsers, FaQuestion, FaLeaf, FaHandshake, FaChalkboardTeacher, FaRegLightbulb, FaPoundSign } from 'react-icons/fa';

// Corporate team building in nature setting - replace with actual image path when available
const heroBackground = 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const CorporateWellbeingContainer = styled.div`
  color: ${theme.colors.textLight};
`;

const HeroSection = styled.div`
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5),
    rgba(${theme.colors.primaryRGB}, 0.8)
  ), url(${heroBackground}) no-repeat center center;
  background-size: cover;
  height: 90vh; 
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 20px;
  
  @media screen and (max-width: 768px) {
    height: 80vh;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin-top: 80px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 24px;
  letter-spacing: 1px;
  font-family: ${theme.fonts.heading};
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroTagline = styled.p`
  font-size: 1.5rem;
  color: ${theme.colors.light};
  margin: 0 auto 40px;
  font-style: italic;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
  
  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContentSection = styled.section`
  background: ${theme.colors.dark};
  padding: ${theme.spacing.section} 0;
`;

const SectionWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 40px;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 60px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -10px;
    left: 0;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${theme.colors.textLight};
  margin-bottom: 25px;
`;

const Emphasis = styled.em`
  display: block;
  font-size: 1.2rem;
  margin: 30px 0;
  color: ${theme.colors.accent};
`;

// Component for image and text layout
const ContentWithImage = styled.div`
  display: flex;
  align-items: center;
  margin: 50px 0;
  flex-direction: ${props => props.imageRight ? 'row-reverse' : 'row'};
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 1;
  padding: ${props => props.imageRight ? '0 0 0 30px' : '0 30px 0 0'};
  
  @media screen and (max-width: 768px) {
    padding: 0;
    margin-bottom: 30px;
    width: 100%;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
  object-fit: cover;
  height: 300px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const OptionsContainer = styled.div`
  margin: 50px 0;
  background: ${theme.colors.primaryDark};
  padding: 30px;
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.boxShadow};
`;

const OptionsTitle = styled.h3`
  font-size: 1.8rem;
  color: ${theme.colors.accent};
  margin-bottom: 25px;
  text-align: center;
`;

const OptionsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const OptionItem = styled.li`
  margin-bottom: 20px;
  font-size: 1.1rem;
  padding-left: 30px;
  position: relative;
  
  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${theme.colors.accent};
  }
`;

const EthosStatement = styled.div`
  text-align: center;
  padding: 20px;
  margin: 40px 0;
  border-top: 1px solid ${theme.colors.accent};
  border-bottom: 1px solid ${theme.colors.accent};
`;

const EthosText = styled.p`
  font-size: 1.3rem;
  font-style: italic;
  color: ${theme.colors.accent};
  line-height: 1.8;
`;

const BookButton = styled.a`
  display: inline-block;
  background: ${theme.colors.accent};
  color: white;
  padding: 15px 30px;
  font-size: 1.1rem;
  border-radius: ${theme.borderRadius};
  text-decoration: none;
  margin: 30px 0;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.accentLight};
    transform: translateY(-3px);
  }
`;

const BenefitsSection = styled.section`
  background: linear-gradient(to bottom, ${theme.colors.primary}, ${theme.colors.primaryDark});
  padding: ${theme.spacing.section} 0;
`;

const BenefitsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BenefitsTitle = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.textLight};
  margin-bottom: 50px;
  text-align: center;
  font-family: ${theme.fonts.heading};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const BenefitItem = styled.div`
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius};
  padding: 25px;
  backdrop-filter: blur(5px);
`;

const BenefitHeading = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.accent};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 15px;
  }
`;

const BenefitText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
`;

const CorporateWellbeingSection = () => {
  return (
    <CorporateWellbeingContainer id="corporate-wellbeing">
      <HeroSection>
        <HeroContent>
          <HeroTitle>Corporate Wellbeing</HeroTitle>
          <HeroTagline>Rebuild your team. Reconnect your people. Reduce burnout.</HeroTagline>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <SectionWrapper>
          <SectionHeading>A Return to Tribe in the Corporate World</SectionHeading>
          
          <Paragraph>
            In today's hybrid, high-stress work culture, teams are more connected than ever and yet more disconnected than they've ever felt.
          </Paragraph>
          
          <Paragraph>
            Remote work, high workloads, and ongoing pressure to perform have quietly frayed the bonds that make teams thrive. The result? 
            Burnout, isolation, poor morale and skyrocketing sick days.
          </Paragraph>
          
          <Paragraph>
            In the UK, mental health-related absenteeism costs businesses over £42 billion each year.
          </Paragraph>
          
          <Paragraph>
            But the solution doesn't lie in more Zoom meetings or surface-level wellness perks. It lies in something more human. More primal.
          </Paragraph>
          
          <ContentWithImage imageRight={true}>
            <ImageContainer imageRight={true}>
              <ContentImage 
                src="https://images.unsplash.com/photo-1527525443983-6e60c75fff46?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                alt="Team gathering around campfire" 
              />
            </ImageContainer>
            <TextContent>
              <Paragraph>
                What I offer is a return to tribe.
              </Paragraph>
              <Paragraph>
                Around the fire, under a tarp shelter, with warm food, fresh air and space to breathe your team begins to feel like a team again. 
                Not in a forced "trust fall" kind of way, but in a real, grounded, meaningful way.
              </Paragraph>
              <Paragraph>
                There is an approach for everyone, even if your team don't like the outdoors we can do a hybrid indoor/outdoor session. 
                Making sure all needs are catered for.
              </Paragraph>
            </TextContent>
          </ContentWithImage>
          
          <SectionHeading>How We Work Together</SectionHeading>
          
          <Paragraph>
            I design and deliver outdoor wellbeing experiences and workshops that help teams:
          </Paragraph>
          
          <OptionsContainer>
            <OptionsList>
              <OptionItem>Reconnect with themselves and each other</OptionItem>
              <OptionItem>Reduce stress and prevent burnout</OptionItem>
              <OptionItem>Build emotional intelligence and communication</OptionItem>
              <OptionItem>Strengthen team cohesion and culture</OptionItem>
            </OptionsList>
            <Paragraph style={{ marginTop: '20px' }}>
              These sessions blend psychotherapy-informed facilitation, nature-based practices, and movement or mindfulness activities 
              tailored to the needs of your people.
            </Paragraph>
          </OptionsContainer>
          
          <Paragraph>
            Options include:
          </Paragraph>
          
          <OptionsList>
            <OptionItem>Half-day or full-day retreats in nature</OptionItem>
            <OptionItem>Fire-side wellbeing workshops on stress, resilience, and emotional regulation</OptionItem>
            <OptionItem>Team-building days that focus on connection, collaboration, and fun</OptionItem>
            <OptionItem>Psychoeducation talks on mental health in the workplace</OptionItem>
            <OptionItem>Follow-up programmes to sustain change over time</OptionItem>
          </OptionsList>
          
          <ContentWithImage imageRight={false}>
            <ImageContainer>
              <ContentImage 
                src={`${process.env.PUBLIC_URL}/images/corporate-group.jpg`}
                alt="Corporate team building outdoors" 
              />
            </ImageContainer>
            <TextContent>
              <Paragraph>
                Whether it's a one-off reset day or a longer-term wellbeing partnership, the aim is simple:
              </Paragraph>
              <Emphasis>
                Bring your people back together. Help them feel seen, supported, and part of something meaningful again.
              </Emphasis>
              <div style={{ textAlign: 'left' }}>
                <BookButton href="#contact">Book a Discovery Call</BookButton>
              </div>
            </TextContent>
          </ContentWithImage>
          
          <EthosStatement>
            <EthosText>
              No PowerPoints. No performance masks.<br />
              Just honest human connection, under the sky.
            </EthosText>
          </EthosStatement>
        </SectionWrapper>
      </ContentSection>
      
      <BenefitsSection>
        <BenefitsContainer>
          <BenefitsTitle>Why It Works</BenefitsTitle>
          
          <BenefitItem>
            <BenefitHeading>
              <FaLeaf /> Nature regulates the nervous system
            </BenefitHeading>
            <BenefitText>
              Helping reduce cortisol, anxiety and fatigue. Research shows just 20 minutes in nature significantly 
              lowers stress hormones and improves wellbeing.
            </BenefitText>
          </BenefitItem>
          
          <BenefitItem>
            <BenefitHeading>
              <FaUsers /> Open-air conversations lower defences
            </BenefitHeading>
            <BenefitText>
              People speak more honestly and connect more deeply when removed from conventional office settings.
              Walking side-by-side rather than face-to-face creates a different dynamic for communication.
            </BenefitText>
          </BenefitItem>
          
          <BenefitItem>
            <BenefitHeading>
              <FaRegLightbulb /> Experiential learning sticks
            </BenefitHeading>
            <BenefitText>
              We remember what we feel, not what we're told. Experiential approaches create lasting change 
              through embodied learning that traditional training can't match.
            </BenefitText>
          </BenefitItem>
          
          <BenefitItem>
            <BenefitHeading>
              <FaHandshake /> Movement + mindfulness = resilience
            </BenefitHeading>
            <BenefitText>
              Embodied strategies your team can take back into daily life. Learning practical techniques for stress 
              management in a natural environment makes them more accessible and memorable.
            </BenefitText>
          </BenefitItem>
          
          <BenefitItem>
            <BenefitHeading>
              <FaChalkboardTeacher /> From intervention to integration
            </BenefitHeading>
            <BenefitText>
              Unlike traditional "one-and-done" team building, these experiences can be structured to create lasting 
              impact with follow-up support and practical applications for the workplace.
            </BenefitText>
          </BenefitItem>
          
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <Emphasis>
              If you're looking to reduce absenteeism, improve staff wellbeing, and strengthen your workplace culture — let's talk. 
              I'll work with you to co-create something that fits your team, your budget, and your goals.
            </Emphasis>
            <BookButton href="#contact">Get in touch to book a free discovery call</BookButton>
          </div>
        </BenefitsContainer>
      </BenefitsSection>
    </CorporateWellbeingContainer>
  );
};

export default CorporateWellbeingSection;